import React from "react";
import { useTranslation } from "react-i18next";
import { Bluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import Button from "../components/Button";

const OTHER_KEY = "integrations.odoo.modules.categories.other";

export default function OdooModulesPanel({ integrationId }) {
    const { t } = useTranslation();
    const [modules, setModules] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [showOther, setShowOther] = React.useState(false);

    const fetchModules = React.useCallback(async () => {
        if (!integrationId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await Bluelagoon.getOdooModules({ integrationId });
            if (res.data?.status === 200) {
                setModules(res.data.data);
            } else {
                setError(res.data?.data?.message || res.data?.message);
            }
        } catch (err) {
            setError(err?.response?.data?.message || err?.message);
        } finally {
            setLoading(false);
        }
    }, [integrationId]);

    React.useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    if (loading) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-500">
                    <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t("integrations.odoo.modules.loading")}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <span className="text-sm text-red-700">{error}</span>
                    <Button variant="ghost" size="sm" onClick={fetchModules}>
                        <Icon name="RefreshCw" className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        );
    }

    if (!modules) return null;

    const allCategories = Object.entries(modules.modules);
    const mainCategories = allCategories.filter(([key]) => key !== OTHER_KEY);
    const otherCategory = allCategories.find(([key]) => key === OTHER_KEY);

    return (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-emerald-900 flex items-center">
                    <Icon name="Package" className="w-4 h-4 mr-2" />
                    {t("integrations.odoo.modules.title")}
                </h4>
                <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                    {t("integrations.odoo.modules.count", { count: modules.totalInstalled })}
                </span>
            </div>
            <div className="space-y-2">
                {mainCategories.map(([category, mods]) => (
                    <div key={category}>
                        <p className="text-xs font-semibold text-emerald-700 mb-1">{t(category)}</p>
                        <div className="flex flex-wrap gap-1">
                            {mods.map((mod) => (
                                <span
                                    key={mod.name}
                                    title={mod.summary}
                                    className="text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded"
                                >
                                    {mod.label}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
                {showOther && otherCategory && (
                    <div>
                        <p className="text-xs font-semibold text-emerald-700 mb-1">{t(OTHER_KEY)}</p>
                        <div className="flex flex-wrap gap-1">
                            {otherCategory[1].map((mod) => (
                                <span
                                    key={mod.name}
                                    title={mod.summary}
                                    className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded"
                                >
                                    {mod.label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {otherCategory && (
                <button
                    onClick={() => setShowOther(!showOther)}
                    className="text-xs text-emerald-600 hover:text-emerald-800 mt-2 flex items-center gap-1"
                >
                    <Icon name={showOther ? "ChevronUp" : "ChevronDown"} className="w-3 h-3" />
                    {showOther
                        ? t("integrations.odoo.modules.showLess")
                        : t("integrations.odoo.modules.showOther", { count: otherCategory[1].length })}
                </button>
            )}
        </div>
    );
}
